'use client';

import { useEffect, useState } from 'react';

import { getLogger } from '@/logger/logger';
import {
  getLaboratoryExamsCoverage,
  LaboratoryExamsCoverage,
} from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-laboratory-exams-coverage.service';

const logger = getLogger('laboratory.exams-coverage');

export type SelectedExamsParam = string | string[] | undefined;

export interface UseSelectedExamsCoverageReturn {
  coverage: LaboratoryExamsCoverage | null;
  isLoading: boolean;
  hasError: boolean;
}

export function parseSelectedExamsParam(param: SelectedExamsParam): string[] {
  const values = Array.isArray(param) ? param : param?.split(',');

  return (values ?? []).map((value) => value.trim()).filter(Boolean);
}

export function useSelectedExamsCoverage(
  facilityId: string,
  examsParam: SelectedExamsParam,
): UseSelectedExamsCoverageReturn {
  const examSlugsKey = parseSelectedExamsParam(examsParam).join(',');
  const isEnabled = Boolean(facilityId) && examSlugsKey.length > 0;

  const [coverage, setCoverage] = useState<LaboratoryExamsCoverage | null>(null);
  const [isLoading, setIsLoading] = useState(isEnabled);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      setCoverage(null);
      setIsLoading(false);
      setHasError(false);

      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setHasError(false);

    const examSlugs = examSlugsKey.split(',');

    getLaboratoryExamsCoverage({ facilityId, examSlugs })
      .then((result) => {
        if (!cancelled) {
          setCoverage(result);
        }
      })
      .catch((error) => {
        logger.error('Failed to resolve the laboratory exams coverage', {
          laboratory: {
            event: 'exams_coverage_failed',
            facility_id: facilityId,
            exam_slugs: examSlugs,
          },
          error,
        });

        if (!cancelled) {
          setCoverage(null);
          setHasError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, examSlugsKey, isEnabled]);

  return { coverage, isLoading, hasError };
}
